import { EmptyState } from '@/components/ui/EmptyState'
import { Spinner } from '@/components/ui/Spinner'
import { ROUTES } from '@/constants/routes'
import {
    Avatar,
    Badge,
    Box,
    Grid,
    Heading,
    HStack,
    IconButton,
    Text,
    VStack,
} from '@chakra-ui/react'
import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { FiArrowLeft, FiMail, FiUser } from 'react-icons/fi'
import { useNavigate, useParams } from 'react-router-dom'
import { useUser } from '../hooks/useUsers'

/**
 * UserDetail Page Component
 * View user details (superadmin only)
 */
export function UserDetail() {
  const { t } = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()

  // Fetch user data
  const { data, isLoading, error } = useUser(id)
  const user = data?.data

  if (isLoading) {
    return <Spinner fullScreen />
  }

  if (error) {
    return (
      <EmptyState
        title="Error loading user"
        description={error.message || 'An error occurred while loading user details.'}
      />
    )
  }

  if (!user) {
    return (
      <EmptyState
        title="User not found"
        description="The requested user does not exist."
      />
    )
  }

  const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ') || user.username
  const initials = user.first_name?.[0] || user.username?.[0] || '?'

  // Determine role
  let roleConfig
  if (user.is_superuser) {
    roleConfig = { label: 'SUPERADMIN', bg: '#DE350B' }
  } else if (user.is_staff) {
    roleConfig = { label: 'STAFF', bg: '#0052CC' }
  } else {
    roleConfig = { label: 'USER', bg: '#5E6C84' }
  }

  return (
    <Box>
      {/* Header */}
      <HStack justify="space-between" mb={6}>
        <HStack spacing={3}>
          <IconButton
            onClick={() => navigate(ROUTES.USERS)}
            variant="ghost"
            size="md"
            aria-label="Back to users"
            borderRadius="sm"
          >
            <Box as={FiArrowLeft} boxSize="20px" />
          </IconButton>
          <Heading size="lg" color="#172B4D">
            {t('users.userDetails')}
          </Heading>
        </HStack>
      </HStack>

      {/* User Info Card */}
      <Box
        bg="white"
        borderRadius="6px"
        border="1px solid #DFE1E6"
        boxShadow="0 1px 1px rgba(9, 30, 66, 0.08)"
        overflow="hidden"
      >
        {/* Header Section with Avatar */}
        <Box
          bg="#F4F5F7"
          p={6}
          borderBottom="1px solid #DFE1E6"
        >
          <HStack spacing={4} align="start">
            <Avatar.Root
              size="xl"
              bg="#0052CC"
              color="white"
              name={fullName}
            >
              {user.avatar ? (
                <Avatar.Image src={user.avatar} alt={fullName} />
              ) : null}
              <Avatar.Fallback fontSize="2xl" fontWeight="600">
                {initials.toUpperCase()}
              </Avatar.Fallback>
            </Avatar.Root>

            <VStack align="start" gap={2} flex={1}>
              <Heading size="xl" color="#172B4D">
                {fullName}
              </Heading>
              <Text fontSize="16px" color="#5E6C84">
                @{user.username}
              </Text>
              <HStack gap={2}>
                <Badge
                  bg={roleConfig.bg}
                  color="white"
                  fontSize="11px"
                  fontWeight="600"
                  px={2}
                  py={1}
                  borderRadius="3px"
                >
                  {roleConfig.label}
                </Badge>
                <Badge
                  bg={user.is_active ? '#E3FCEF' : '#FFEBE6'}
                  color={user.is_active ? '#006644' : '#BF2600'}
                  fontSize="11px"
                  fontWeight="600"
                  px={2}
                  py={1}
                  borderRadius="3px"
                >
                  {user.is_active ? 'ACTIVE' : 'INACTIVE'}
                </Badge>
              </HStack>
            </VStack>
          </HStack>
        </Box>

        {/* Details Section */}
        <Box p={6}>
          <Grid templateColumns="repeat(auto-fit, minmax(300px, 1fr))" gap={6}>
            {/* Contact Information */}
            <VStack align="start" gap={4}>
              <Heading size="sm" color="#5E6C84" fontWeight="600" textTransform="uppercase" fontSize="12px">
                Contact Information
              </Heading>

              <VStack align="start" gap={3} w="full">
                <HStack gap={3}>
                  <Box as={FiMail} color="#5E6C84" boxSize="16px" />
                  <VStack align="start" gap={0}>
                    <Text fontSize="12px" color="#5E6C84">Email</Text>
                    <Text fontSize="14px" color="#172B4D" fontWeight="500">
                      {user.email || '—'}
                    </Text>
                  </VStack>
                </HStack>

                <HStack gap={3}>
                  <Box as={FiUser} color="#5E6C84" boxSize="16px" />
                  <VStack align="start" gap={0}>
                    <Text fontSize="12px" color="#5E6C84">Username</Text>
                    <Text fontSize="14px" color="#172B4D" fontWeight="500">
                      {user.username}
                    </Text>
                  </VStack>
                </HStack>
              </VStack>
            </VStack>

            {/* Account Details */}
            <VStack align="start" gap={4}>
              <Heading size="sm" color="#5E6C84" fontWeight="600" textTransform="uppercase" fontSize="12px">
                Account Details
              </Heading>

              <VStack align="start" gap={3} w="full">
                <VStack align="start" gap={0}>
                  <Text fontSize="12px" color="#5E6C84">User ID</Text>
                  <Text fontSize="14px" color="#172B4D" fontWeight="500">
                    #{user.id}
                  </Text>
                </VStack>

                <VStack align="start" gap={0}>
                  <Text fontSize="12px" color="#5E6C84">Date Joined</Text>
                  <Text fontSize="14px" color="#172B4D" fontWeight="500">
                    {user.date_joined ? format(new Date(user.date_joined), 'dd MMMM yyyy, HH:mm') : '—'}
                  </Text>
                </VStack>

                <VStack align="start" gap={0}>
                  <Text fontSize="12px" color="#5E6C84">Last Login</Text>
                  <Text fontSize="14px" color="#172B4D" fontWeight="500">
                    {user.last_login ? format(new Date(user.last_login), 'dd MMMM yyyy, HH:mm') : 'Never'}
                  </Text>
                </VStack>
              </VStack>
            </VStack>

            {/* Permissions */}
            <VStack align="start" gap={4}>
              <Heading size="sm" color="#5E6C84" fontWeight="600" textTransform="uppercase" fontSize="12px">
                Permissions
              </Heading>

              <VStack align="start" gap={2} w="full">
                <HStack>
                  <Box
                    w="16px"
                    h="16px"
                    borderRadius="3px"
                    bg={user.is_superuser ? '#E3FCEF' : '#F4F5F7'}
                    border="2px solid"
                    borderColor={user.is_superuser ? '#006644' : '#DFE1E6'}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    {user.is_superuser && (
                      <Box w="8px" h="8px" bg="#006644" borderRadius="2px" />
                    )}
                  </Box>
                  <Text fontSize="14px" color="#172B4D">
                    Superuser
                  </Text>
                </HStack>

                <HStack>
                  <Box
                    w="16px"
                    h="16px"
                    borderRadius="3px"
                    bg={user.is_staff ? '#E3FCEF' : '#F4F5F7'}
                    border="2px solid"
                    borderColor={user.is_staff ? '#006644' : '#DFE1E6'}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    {user.is_staff && (
                      <Box w="8px" h="8px" bg="#006644" borderRadius="2px" />
                    )}
                  </Box>
                  <Text fontSize="14px" color="#172B4D">
                    Staff
                  </Text>
                </HStack>

                <HStack>
                  <Box
                    w="16px"
                    h="16px"
                    borderRadius="3px"
                    bg={user.is_active ? '#E3FCEF' : '#F4F5F7'}
                    border="2px solid"
                    borderColor={user.is_active ? '#006644' : '#DFE1E6'}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    {user.is_active && (
                      <Box w="8px" h="8px" bg="#006644" borderRadius="2px" />
                    )}
                  </Box>
                  <Text fontSize="14px" color="#172B4D">
                    Active
                  </Text>
                </HStack>
              </VStack>
            </VStack>
          </Grid>
        </Box>
      </Box>
    </Box>
  )
}

export default UserDetail
